/**
 * Calcula la duración en SEGUNDOS entre dos fechas (máxima precisión)
 * Horario laboral: 8:00 AM - 5:00 PM (9 horas totales - 1 hora break = 8 horas)
 * Break: 1:00 PM - 2:00 PM
 * Solo cuenta días laborales (Lunes - Viernes)
 */

const WORK_START_HOUR = 8; // 8 AM
const WORK_END_HOUR = 17; // 5 PM
const BREAK_START_HOUR = 13; // 1 PM
const BREAK_END_HOUR = 14; // 2 PM
const DAILY_WORK_SECONDS = 8 * 3600; // 8 horas = 28800 segundos

/**
 * Verifica si una fecha es día laboral (Lunes-Viernes)
 */
function isWorkday(date: Date): boolean {
  const day = date.getDay();
  return day >= 1 && day <= 5; // 1 = Lunes, 5 = Viernes
}

/**
 * Obtiene la fecha sin hora (solo año, mes, día)
 */
function getDateOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Convierte una fecha a segundos desde medianoche
 */
function dateToSecondsInDay(date: Date): number {
  return date.getHours() * 3600 + date.getMinutes() * 60 + date.getSeconds();
}

/**
 * Calcula los segundos trabajados entre dos momentos del mismo día
 */
function calculateSecondsInDay(startSeconds: number, endSeconds: number): number {
  // Convertir a horas decimales para facilitar cálculo
  const startHour = startSeconds / 3600;
  const endHour = endSeconds / 3600;

  // Limitar al horario laboral
  const workStart = Math.max(startHour, WORK_START_HOUR);
  const workEnd = Math.min(endHour, WORK_END_HOUR);

  if (workStart >= workEnd) return 0;

  let totalSeconds = (workEnd - workStart) * 3600;

  // Descontar el break si está dentro del rango
  if (workStart < BREAK_END_HOUR && workEnd > BREAK_START_HOUR) {
    const breakStart = Math.max(workStart, BREAK_START_HOUR);
    const breakEnd = Math.min(workEnd, BREAK_END_HOUR);
    totalSeconds -= (breakEnd - breakStart) * 3600;
  }

  return Math.max(0, Math.floor(totalSeconds));
}

/**
 * Cuenta los días laborales completos entre dos fechas (excluyendo primer y último día)
 */
function countFullWorkdays(startDate: Date, endDate: Date): number {
  let count = 0;
  const current = new Date(startDate);
  current.setDate(current.getDate() + 1); // Empezar desde el día siguiente

  while (current < endDate) {
    if (isWorkday(current)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }

  return count;
}

/**
 * Calcula la duración total en SEGUNDOS (máxima precisión)
 * @returns Duración en segundos
 */
export function calculateLeaveDuration(
  startDate: Date,
  endDate: Date,
  allDay: boolean = false
): number {
  // Si es todo el día, usar horario completo
  if (allDay) {
    const start = new Date(startDate);
    start.setHours(WORK_START_HOUR, 0, 0, 0);
    
    const end = new Date(endDate);
    end.setHours(WORK_END_HOUR, 0, 0, 0);
    
    return calculateLeaveDuration(start, end, false);
  }

  const startDateOnly = getDateOnly(startDate);
  const endDateOnly = getDateOnly(endDate);

  // Caso 1: Mismo día
  if (startDateOnly.getTime() === endDateOnly.getTime()) {
    if (!isWorkday(startDate)) return 0;
    
    const startSeconds = dateToSecondsInDay(startDate);
    const endSeconds = dateToSecondsInDay(endDate);
    
    return calculateSecondsInDay(startSeconds, endSeconds);
  }

  // Caso 2: Múltiples días
  let totalSeconds = 0;

  // Segundos del primer día (si es día laboral)
  if (isWorkday(startDate)) {
    const startSeconds = dateToSecondsInDay(startDate);
    const endOfDaySeconds = WORK_END_HOUR * 3600; // 5 PM
    totalSeconds += calculateSecondsInDay(startSeconds, endOfDaySeconds);
  }

  // Días completos en el medio
  const fullWorkdays = countFullWorkdays(startDateOnly, endDateOnly);
  totalSeconds += fullWorkdays * DAILY_WORK_SECONDS;

  // Segundos del último día (si es día laboral)
  if (isWorkday(endDate)) {
    const startOfDaySeconds = WORK_START_HOUR * 3600; // 8 AM
    const endSeconds = dateToSecondsInDay(endDate);
    totalSeconds += calculateSecondsInDay(startOfDaySeconds, endSeconds);
  }

  return Math.max(0, totalSeconds);
}

/**
 * Convierte segundos a objeto con días, horas, minutos, segundos
 */
export function secondsToComponents(totalSeconds: number): {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
  totalHours: number;
} {
  const days = Math.floor(totalSeconds / DAILY_WORK_SECONDS);
  const remainingAfterDays = totalSeconds % DAILY_WORK_SECONDS;
  
  const hours = Math.floor(remainingAfterDays / 3600);
  const minutes = Math.floor((remainingAfterDays % 3600) / 60);
  const seconds = Math.floor(remainingAfterDays % 60);
  
  const totalHours = totalSeconds / 3600;

  return { days, hours, minutes, seconds, totalHours };
}

/**
 * Formatea la duración en un string legible con precisión
 * Ejemplo: 29700 segundos -> "1 día, 0 horas, 15 minutos"
 */
export function formatDuration(totalSeconds: number): string {
  if (totalSeconds === 0) return "0 horas";
  
  const { days, hours, minutes, seconds } = secondsToComponents(totalSeconds);

  const parts: string[] = [];
  
  if (days > 0) {
    parts.push(`${days} día${days > 1 ? 's' : ''}`);
  }
  
  if (hours > 0) {
    parts.push(`${hours} hora${hours > 1 ? 's' : ''}`);
  }
  
  if (minutes > 0) {
    parts.push(`${minutes} minuto${minutes > 1 ? 's' : ''}`);
  }

  // Solo mostrar segundos si la duración es menor a 1 hora
  if (totalSeconds < 3600 && seconds > 0) {
    parts.push(`${seconds} segundo${seconds > 1 ? 's' : ''}`);
  }

  return parts.join(', ');
}

/**
 * Formatea en formato corto: "1d 2h 30m"
 */
export function formatDurationShort(totalSeconds: number): string {
  if (totalSeconds === 0) return "0h";
  
  const { days, hours, minutes } = secondsToComponents(totalSeconds);

  const parts: string[] = [];
  
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);

  return parts.join(' ');
}